import React, { useEffect, useState } from 'react';
import { Card, Input, Tag, Space } from 'antd';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import CustomTable from 'common/Content/CustomTable';
import Layout from '@/common/Layout';
import styles from '@/common/Layout/index.module.less';
import useServerPagination from '@/hooks/useServerPagination';
import { getRetestDueBatches } from '../../redux/slice';

const { Search } = Input;

/**
 * Batches approaching their retest date.
 *
 * The point of the screen is to show QC and WH which batches need a retest
 * BEFORE the nightly cron flips them to Quarantine — which otherwise happens
 * underneath whatever the batch is being used for. It is read-only for every
 * role: no batch-number link to the TRF and no Raise retest action.
 *
 * Only `Approved` batches appear, because that is exactly what the cron selects
 * (`restart_batches`). A batch already in Quarantine or Expired is past the point
 * this screen exists to prevent, and including them buries the handful of
 * actionable rows: in production the 7-day window holds 2 Approved batches
 * against 132 Quarantine, 33 Expired and 17 Partially ID Approved.
 */
const BatchesForRetest = () => {
  const dispatch = useDispatch();
  const { page, pageSize, onChange } = useServerPagination();
  const [search, setSearch] = useState('');
  const [windowDays, setWindowDays] = useState(7);
  const [batches, setBatches] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    load();
  }, [page, pageSize, search, windowDays]);

  const load = () => {
    setLoading(true);
    dispatch(
      getRetestDueBatches({
        page,
        per_page: pageSize,
        search,
        within_days: windowDays
      })
    ).then((response) => {
      setLoading(false);
      if (response?.payload?.success) {
        setBatches(response?.payload?.data || []);
        setPagination(response?.payload?.pagination || null);
      } else {
        setBatches([]);
        setPagination(null);
      }
    });
  };

  /* Negative means the date has already passed. Those are the urgent ones, and
     the API already sorts them to the top. */
  const renderDays = (days) => {
    const n = Number(days);
    if (!Number.isFinite(n)) return '—';
    if (n < 0)
      return <Tag color="#dc3545">{`OVERDUE by ${Math.abs(n)} day(s)`}</Tag>;
    if (n === 0) return <Tag color="#dc3545">DUE TODAY</Tag>;
    return <Tag color={n <= 2 ? '#FF9800' : '#17a2b8'}>{`${n} day(s)`}</Tag>;
  };

  const columns = [
    {
      title: 'Batch Number',
      dataIndex: 'inhouse_batch_number',
      key: 'inhouse_batch_number',
      ellipsis: true,
      width: '150px',
      render: (text) => text || 'NA'
    },
    {
      title: 'Material Code',
      dataIndex: 'material',
      key: 'material_code',
      ellipsis: true,
      width: '130px',
      render: (material) => material?.code || 'NA'
    },
    {
      title: 'Material Name',
      dataIndex: 'material',
      key: 'material_name',
      ellipsis: true,
      width: '200px',
      render: (material) => material?.name || 'NA'
    },
    {
      title: 'Closing Stock',
      dataIndex: 'closing_stock',
      key: 'closing_stock',
      ellipsis: true,
      width: '120px',
      render: (text) => text ?? 'NA'
    },
    {
      title: 'Retest Date',
      dataIndex: 'inhouse_retest_date',
      key: 'inhouse_retest_date',
      ellipsis: true,
      width: '130px',
      render: (text) => (text ? moment(text).format('DD MMM YYYY') : 'NA')
    },
    {
      title: 'Due In',
      dataIndex: 'days_to_retest',
      key: 'days_to_retest',
      ellipsis: true,
      width: '160px',
      render: (text) => renderDays(text)
    },
    {
      title: 'Cycles',
      dataIndex: 'test_requests',
      key: 'cycles',
      ellipsis: true,
      width: '90px',
      render: (requests) => requests?.length || '—'
    }
  ];

  return (
    <Layout sider={false}>
      <Card
        className={`${styles.cardStyles_approver}`}
        title={<span>Batches for Re-test</span>}
        extra={
          <Space>
            <Input
              type="number"
              min={1}
              value={windowDays}
              style={{ width: '90px' }}
              onChange={(e) => setWindowDays(Number(e.target.value) || 7)}
              suffix="days"
            />
            <Search
              onSearch={(value) => setSearch(value)}
              className={`w-100 ${styles.searchbar}`}
              justify="start"
              align="start"
              placeholder="Search by Batch number"
            />
          </Space>
        }
      >
        <div className={`${styles.table_container}`}>
          <CustomTable
            loading={loading}
            columns={columns}
            dataSource={batches}
            rowKey={(record) => record?.id}
            pagination={{
              size: 'small',
              showSizeChanger: true,
              current: page,
              pageSize,
              total: pagination?.total_records || 0,
              onChange: (currentPage, size) => onChange(currentPage, size)
            }}
          />
        </div>
      </Card>
    </Layout>
  );
};

export default BatchesForRetest;
